/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:21:50
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-23 14:50:00
 * @ Description: Dialog to send contact agent email
 */

import React, { useEffect, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Grid,
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import {
  find,
  cloneDeep,
  merge,
  head,
  map,
  get,
  includes,
  clone,
  pullAt,
} from '@/utils/lodash'

import withSession from '@/housefolios-components/Session/withSession'
import ContactAgentStepper from '../ContactAgentStepper'
import housefoliosLogo from '@/assets/images/HousefoliosCloudTransparent.png'

import { enqueueSnackbar } from 'notistack'
import { useMutation } from '@apollo/client/react'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { CONTACT_AGENT } from '../mutations'
import BlockUi from 'react-block-ui'
import CheckIcon from '@mui/icons-material/Check'
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone'
import CloudUploadTwoToneIcon from '@mui/icons-material/CloudUploadTwoTone'
import { PacmanLoader } from 'react-spinners'
import { NumericFormat } from 'react-number-format'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import { UPDATE_ORGANIZATION } from '@/housefolios-components/OrganizationSettings/mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { useDropzone } from 'react-dropzone'
import AddOption from '@/housefolios-components/Dialogs/AddOption'
import RemoveOption from '@/housefolios-components/Dialogs/RemoveOption'
import { pepContactAgentEmail } from '@/constants/emails'
import useAffiliate from '@/hooks/useAffiliate'
import { useShareEmails } from '@/hooks/useShareEmails'
import ShareEmailsAutocomplete from '@/housefolios-components/FormComponents/ShareEmailsAutocomplete'
import RichTextEditor from '@/housefolios-components/RichTextEditor'
import FileUpload from '@/housefolios-components/PropertyContainer/PropertyDashboard/FileUpload'

function ContactAgentComponent(props) {
  const {
    session: {
      me: {
        // profile: { firstname = '', lastname = '' },
        // email = '',
        // phone = '',
        activeOrg = {},
        profile = {},
      },
    },
    property: { _id, address = '', city = '', state = '', zip = '' },
  } = props

  const targetMarket = find(
    activeOrg.marketPlaces,
    (marketplace) => props.marketplace === marketplace.url,
  )
  let contactEmail = ''
  if (targetMarket) contactEmail = targetMarket.contactEmail

  const preferences = merge(
    cloneDeep(DEFAULT_PREFERENCES),
    props?.session?.me?.activeOrg?.memberPreferences,
    props?.session?.me?.activeOrg?.preferences,
  )

  const { contactAgentPreferences = {}, contactAgentTemplate = {} } =
    preferences

  const [file, setFile] = useState([])
  const uploadFile = (files) => {
    let newFiles = clone(file) || []
    map(files, (file) => {
      newFiles.push(file)
    })
    setFile(newFiles)
  }

  const deleteFile = (index) => {
    let newFiles = clone(file) || []
    pullAt(newFiles, index)
    setFile(newFiles)
  }

  const [saveDefaultValue, setSaveDefaultValue] = useState(false)

  const [openAddComp, setOpenAddComp] = useState(false)
  const [loader, setLoader] = useState(false)
  const handleOpenAddComp = () => {
    setOpenAddComp(true)
  }
  const handleCloseAddComp = () => {
    setOpenAddComp(false)
  }

  const [firstname, setFirstname] = useState(
    props.session.me.profile.firstname,
  )
  const [lastname, setLastname] = useState(
    props.session.me.profile.lastname,
  )
  const [email, setEmail] = useState(props.session.me.email)
  const [phone, setPhone] = useState(props.session.me.phone)

  const [message, setMessage] = useState('')

  const [offerPrice, setOfferPrice] = useState(
    props.defaultOffer
      ? props.defaultOffer
      : props.property.acquisition
        ? props.property.acquisition.analysis.offerPrice
        : '',
  )

  const [conditions, setConditions] = useState(
    contactAgentPreferences?.conditions ||
      contactAgentTemplate?.conditions ||
      '',
  )
  const [statements, setStatements] = useState(
    contactAgentPreferences?.statements ||
      contactAgentTemplate?.statements ||
      '',
  )
  const [inspectionPeriod, setInspectionPeriod] = useState(
    contactAgentPreferences?.inspectionPeriod ||
      contactAgentTemplate?.inspectionPeriod ||
      '',
  )
  const [daysToClose, setDaysToClose] = useState(
    contactAgentPreferences?.daysToClose ||
      contactAgentTemplate?.daysToClose ||
      '',
  )
  const [earnestMoney, setEarnestMoney] = useState(
    contactAgentPreferences?.earnestMoney ||
      contactAgentTemplate?.earnestMoney ||
      '',
  )
  const [entityName, setEntityName] = useState(activeOrg.name)
  const [entityAddress, setEntityAddress] = useState(
    activeOrg.address,
  )
  const [entityCity, setEntityCity] = useState(activeOrg.city)
  const [entityState, setEntityState] = useState(activeOrg.state)
  const [entityZip, setEntityZip] = useState(activeOrg.zip)
  const [entityEmail, setEntityEmail] = useState(activeOrg.email)
  const [entitySigner, setEntitySigner] = useState(
    profile.firstname + ' ' + profile.lastname,
  )

  const [buyerSellerProfile, setBuyerSellerProfile] = useState(
    head(activeOrg?.buyerSellerProfiles)?.value || '',
  )

  const selectedBuyerSellerProfile = find(
    activeOrg?.buyerSellerProfiles || [],
    (profile) => {
      return profile.value === buyerSellerProfile
    },
  )

  const [{ affiliate, affiliateLoading, urlAffiliate }] =
    useAffiliate({
      memberValue: activeOrg?.member,
    })

  const getPepEmailParams = () => {
    let linkStart = ''

    const hostname = import.meta.env.VITE_HOSTNAME

    if (hostname == 'localhost') {
      linkStart = `http://localhost:3000/`
    } else if (hostname == 'dev-admin.housefolios.com') {
      linkStart = `https://dev-admin.housefolios.com/`
    } else {
      linkStart = `https://app.housefolios.com/`
    }

    return {
      headerLogo:
        selectedBuyerSellerProfile?.logoImage?.url ||
        get(affiliate, 'sidebar.sidebarMemberSmallImage.0.url') ||
        housefoliosLogo,
      hideSocialIcons: true,
      hideOrgInfo: true,
      affiliateName: affiliate?.companyName,
      affiliateURL: urlAffiliate,
      linkStart,
    }
  }

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const [introduction, setIntroduction] =
    useState(`<h1>We're Interested in Your Property</h1>
          <p>Dear Homeowner,</p>
          <p>
            Thank you for considering us as potential buyers for your property. After reviewing your listing at ${address} ${city}, ${state} ${zip}, we are excited about the opportunity to present you with an offer. We understand that selling a home is a significant decision, and our goal is to provide a smooth and transparent experience.
          </p>`)

  const { analysis = {} } = props.property.acquisition || {}

  useEffect(() => {
    setOfferPrice(
      props.defaultOffer
        ? props.defaultOffer
        : analysis.offerPrice || 0,
    )
  }, [analysis.offerPrice, props.defaultOffer])

  const toEmails = useShareEmails()
  const ccEmails = useShareEmails()
  const bccEmails = useShareEmails()

  const [contactAgent] = useMutation(CONTACT_AGENT)
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION)

  const contact = (event) => {
    event?.preventDefault?.()
    if (props.session.me.activeOrg.isRealeflow) {
      const contactEmail = toEmails.collectEmails()
      const ccEmail = ccEmails.collectEmails()
      const bccEmail = bccEmails.collectEmails()
      if (!contactEmail.length) {
        enqueueSnackbar('Enter at least one valid email address', {
          variant: 'warning',
          autoHideDuration: 4000,
        })
        return
      }
      setLoader(true)
      contactAgent({
        variables: {
          propertyId: _id,
          address,
          contactEmail,
          ccEmail,
          bccEmail,
          html: pepContactAgentEmail(
            getPepEmailParams(),
            address,
            city,
            state,
            zip,
            currencyFormat.format(offerPrice),
            selectedBuyerSellerProfile?.conditions,
            selectedBuyerSellerProfile?.statements,
            selectedBuyerSellerProfile?.inspectionPeriod,
            selectedBuyerSellerProfile?.daysToClose,
            selectedBuyerSellerProfile?.earnestMoney,
            selectedBuyerSellerProfile?.value,
            selectedBuyerSellerProfile?.address,
            selectedBuyerSellerProfile?.city,
            selectedBuyerSellerProfile?.state,
            selectedBuyerSellerProfile?.zip,
            selectedBuyerSellerProfile?.email,
            selectedBuyerSellerProfile?.phone,
            selectedBuyerSellerProfile?.signer,
            introduction,
          ),
          entityEmail: selectedBuyerSellerProfile?.email || '',
          pep: true,
          base64: map(file, (f) => f.url),
        },
      }).then(({ data  }) => {
        enqueueSnackbar(`Message sent successfully`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        setLoader(false)
        handleCloseAddComp()
        updateOrganization({
          variables: {
            id: props.session.me.activeOrg._id,
            organizationInput: {
              preferences: {
                ...preferences,
                contactAgentPreferences: {
                  conditions,
                  statements,
                  inspectionPeriod,
                  daysToClose,
                  earnestMoney,
                },
              },
            },
          },
          refetchQueries: [{ query: GET_ME }],
        })
        toEmails.resetEmails()
        ccEmails.resetEmails()
        bccEmails.resetEmails()
        setIntroduction(`<h1>We're Interested in Your Property</h1>
          <p>Dear Homeowner,</p>
          <p>
            Thank you for considering us as potential buyers for your property. After reviewing your listing at ${address} ${city}, ${state} ${zip}, we are excited about the opportunity to present you with an offer. We understand that selling a home is a significant decision, and our goal is to provide a smooth and transparent experience.
          </p>`)
        setFile([])
      })
    } else {
      setLoader(true)
      contactAgent({
        variables: {
          message,
          propertyId: _id,
          contactEmail: [contactEmail],
          firstname,
          lastname,
          phone,
          email,
          address,
          city,
          state,
          zip,
          RWN: true,
        },
      }).then(({ data  }) => {
        enqueueSnackbar(`Message sent successfully`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        setLoader(false)
        handleCloseAddComp()
      })
    }
  }

  const disabled = props.property.offerMade

  // const files = file.map((file) => (
  //   <ListItem
  //
  //     key={file.path}
  //   >
  //     <span>{file.path}</span>{' '}
  //     <span>
  //       {file.size} bytes
  //     </span>
  //   </ListItem>
  // ))

  return (
    <>
      {/* <Button onClick={handleOpenAddComp}>
        <AddTwoToneIcon />
      </Button> */}
      {props.buttonLabel && (
        <Button
          variant={props.ctaVariant || 'outlined'}
          size={props.ctaSize || 'small'}
          onClick={handleOpenAddComp}
          disabled={disabled || props.ctaDisabled}
          title={props.ctaTitle}
        >
          {disabled ? 'Offer Submitted' : props.buttonLabel}
        </Button>
      )}
      {props.marketplace && (
        <Button
          fullWidth
          variant="contained"
          onClick={handleOpenAddComp}
        >
          Contact Agent
        </Button>
      )}
      {props.action && (
        <ListItemButton
          onClick={handleOpenAddComp}
          disabled={disabled}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            cursor: "pointer",
            columnGap: "8px",
            "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
          }}
        >
          <Box sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "8px",
            minWidth: 0,
            flex: "1 1 auto",
            justifyContent: "flex-start",
            "& > .MuiBox-root:first-of-type": {
              flex: "0 0 auto",
              width: 16,
              minWidth: 16,
              marginRight: 0,
            },
          }}>
            <Box sx={{
              width: 16,
              minWidth: 16,
              flexShrink: 0,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 10,
              fontSize: "1.15rem",
              "& > svg": { width: "1em", height: "1em" },
              ".Mui-selected > &": { opacity: "1 !important" },
            }}>
              <FontAwesomeIcon icon={['fas', 'file-invoice-dollar']} />
            </Box>
            <span>{disabled ? 'Offer Submitted' : 'Make Offer'}</span>
          </Box>
        </ListItemButton>
      )}
      {props.propertyPage && (
        <Button data-tour="make-offer-button" fullWidth variant="contained" onClick={handleOpenAddComp} disabled={props.canDrag || disabled} sx={[{
          boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
          color: "#fff",
          backgroundColor: "#368cff",
          backgroundImage: "none",
          borderColor: "#368cff",
          "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
          "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
          "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
          "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
          "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
          transition: "all 0.2s ease-in-out !important",
          transform: "scale(1) !important",
          position: "relative",
          zIndex: "0",
          "&:hover": { transform: "scale(1.08)", zIndex: 3 },
          fontWeight: 700,
          textTransform: "uppercase",
        }, { fontSize: "0.875rem !important" }]}>
          {disabled ? 'Offer Submitted' : 'Make Offer'}
        </Button>
      )}
      {props.propertySummary && (
        // <Button fullWidth variant='contained' color="primary" onClick={handleOpenAddComp}>
        <Button
          fullWidth
         
          onClick={handleOpenAddComp}
          disabled={disabled}
         sx={{
           backgroundColor: "rgba(65, 145, 255, 0.15)",
           color: "#4191ff",
           borderColor: "transparent",
           "&:hover, &.active": { backgroundColor: "#4191ff", color: "#fff" },
           "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
         }}>
          {disabled ? 'Offer Submitted' : 'Make Offer'}
        </Button>
      )}

      <Dialog
        open={openAddComp}
        onClose={handleCloseAddComp}
        // fullScreen={props?.session?.me?.activeOrg?.member !== 'RWN'}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        maxWidth="lg"
        fullWidth={true}
      >
        {props?.session?.me?.activeOrg?.member !== 'RWN' &&
          !props.session.me.activeOrg.isRealeflow && (
            <>
              <DialogTitle>
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h6">
                    Make Offer Wizard
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleCloseAddComp}
                  >
                    Cancel
                  </Button>
                </Grid>
              </DialogTitle>
              <ContactAgentStepper
                property={props.property}
                handleCloseAddComp={handleCloseAddComp}
                marketplace={props.marketplace}
              />
            </>
          )}
        {props.session.me.activeOrg.isRealeflow && (
          <ValidatorForm onSubmit={contact}>
            <DialogTitle>
              <Typography>Contact Seller</Typography>
            </DialogTitle>
            <CardContent>
              <BlockUi
                blocking={loader}
                loader={
                  <PacmanLoader
                    color={'var(--primary)'}
                    loading={true}
                  />
                }
                message={
                  <Box sx={{ color: '#368cff' }}>Sending Message</Box>
                }
              >
                <Grid container direction="row" spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Grid size={12}>
                      <TextField
                        name="buyerSellerProfiles"
                        select
                        fullWidth={true}
                        variant="outlined"
                        margin="dense"
                        label="Buyer/Seller Profile"
                        value={
                          includes(
                            activeOrg?.buyerSellerProfiles || [],
                            buyerSellerProfile,
                          ) ||
                          find(activeOrg?.buyerSellerProfiles || [], {
                            value: buyerSellerProfile,
                          })
                            ? buyerSellerProfile
                            : ''
                        }
                        required
                        onChange={(event) =>
                          setBuyerSellerProfile(event.target.value)
                        }
                        slotProps={{
                          select: {
                            renderValue: (x) => {
                              let option = find(
                                activeOrg?.buyerSellerProfiles,
                                {
                                  value: x,
                                },
                              )
                              if (option) return option.label
                              else if (
                                includes(
                                  activeOrg?.buyerSellerProfiles,
                                  x,
                                )
                              )
                                return x
                              else return null
                            },
                          },
                        }}
                      >
                        <MenuItem key="" value="">
                          No Selected Value
                        </MenuItem>
                        {map(
                          activeOrg?.buyerSellerProfiles || [],
                          (option) =>
                            option.value ? (
                              option.tip ? (
                                <Tooltip
                                  title={option.tip}
                                  key={option.value}
                                  value={option.value}
                                >
                                  <MenuItem>
                                    <Grid
                                      container
                                      sx={{
                                        justifyContent:
                                          'space-between',
                                      }}
                                    >
                                      <Grid>
                                        <Typography>
                                          {option.title}
                                        </Typography>
                                      </Grid>
                                      <Grid>
                                        <Typography>
                                          {option.label}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </MenuItem>
                                </Tooltip>
                              ) : (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  <Grid
                                    container
                                    sx={{
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Grid>{option.label}</Grid>
                                    <Typography style={{ flex: 1 }} />
                                    <AddOption
                                      edit
                                      currentOption={option}
                                      currentValue={
                                        buyerSellerProfile
                                      }
                                      addOption={
                                        'buyerSellerProfiles'
                                      }
                                      addOptionLabel={
                                        'Buyer/Seller Profile'
                                      }
                                      addOptionTarget={
                                        'buyerSellerProfile'
                                      }
                                      onChangeProperty={(event) =>
                                        setBuyerSellerProfile(
                                          event.target.value,
                                        )
                                      }
                                    />
                                    <RemoveOption
                                      removeOption={
                                        'buyerSellerProfiles'
                                      }
                                      removeOptionLabel={
                                        'Buyer/Seller Profile'
                                      }
                                      removeOptionValue={option.value}
                                      removeOptionItemLabel={
                                        option.label
                                      }
                                    />
                                  </Grid>
                                </MenuItem>
                              )
                            ) : (
                              <MenuItem key={option} value={option}>
                                <Grid
                                  container
                                  sx={{
                                    alignItems: 'center',
                                  }}
                                >
                                  <Grid>{option}</Grid>
                                  <Typography style={{ flex: 1 }} />
                                  <AddOption
                                    edit
                                    currentOption={option}
                                    currentValue={buyerSellerProfile}
                                    addOption={'buyerSellerProfiles'}
                                    addOptionLabel={
                                      'Buyer/Seller Profile'
                                    }
                                    addOptionTarget={
                                      'buyerSellerProfile'
                                    }
                                    onChangeProperty={(event) =>
                                      setBuyerSellerProfile(
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <RemoveOption
                                    removeOption={
                                      'buyerSellerProfiles'
                                    }
                                    removeOptionLabel={
                                      'Buyer/Seller Profile'
                                    }
                                    removeOptionValue={option}
                                  />
                                </Grid>
                              </MenuItem>
                            ),
                        )}
                        <AddOption
                          currentValue={buyerSellerProfile}
                          addOption={'buyerSellerProfiles'}
                          addOptionLabel={'Buyer/Seller Profile'}
                          addOptionTarget={'buyerSellerProfile'}
                          onChangeProperty={(event) =>
                            setBuyerSellerProfile(event.target.value)
                          }
                        />
                      </TextField>
                    </Grid>
                    <Grid size={12}>
                      <Typography>
                        Which email would you like to send the offer
                        to?
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <ShareEmailsAutocomplete
                        shareEmails={toEmails}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Typography>CC Emails</Typography>
                    </Grid>
                    <Grid size={12}>
                      <ShareEmailsAutocomplete
                        shareEmails={ccEmails}
                        id="ccemails"
                        label="CC Emails"
                      />
                    </Grid>
                    <Grid size={12}>
                      <Typography>BCC Emails</Typography>
                    </Grid>
                    <Grid size={12}>
                      <ShareEmailsAutocomplete
                        shareEmails={bccEmails}
                        id="bccemails"
                        label="BCC Emails"
                      />
                    </Grid>
                    <Grid size={12}>
                      <RichTextEditor
                        variant="tall"
                        value={introduction}
                        onChange={(value) => {
                          setIntroduction(value)
                        }}
                        placeholder="This is my message to the seller"
                      />
                    </Grid>
                    <Grid size={12} style={{ marginTop: 40 }}>
                      <FileUpload
                        documentId={_id}
                        files={file}
                        uploadFile={uploadFile}
                        deleteFile={deleteFile}
                        filetypes={['image/*']}
                        notSave
                        // maxFiles={1}
                        buttonText="Add Files"
                        button
                        collection="Profiles"
                      />
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Grid
                      size={12}
                      style={{
                        height: '100%',
                      }}
                    >
                      <iframe
                        srcDoc={pepContactAgentEmail(
                          getPepEmailParams(),
                          address,
                          city,
                          state,
                          zip,
                          currencyFormat.format(offerPrice),
                          selectedBuyerSellerProfile?.conditions,
                          selectedBuyerSellerProfile?.statements,
                          selectedBuyerSellerProfile?.inspectionPeriod,
                          selectedBuyerSellerProfile?.daysToClose,
                          selectedBuyerSellerProfile?.earnestMoney,
                          selectedBuyerSellerProfile?.value,
                          selectedBuyerSellerProfile?.address,
                          selectedBuyerSellerProfile?.city,
                          selectedBuyerSellerProfile?.state,
                          selectedBuyerSellerProfile?.zip,
                          selectedBuyerSellerProfile?.email,
                          selectedBuyerSellerProfile?.phone,
                          selectedBuyerSellerProfile?.signer,
                          introduction,
                        )}
                        title="Email Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </BlockUi>
            </CardContent>
            <Divider />
            <CardActions>
              <Typography style={{ flex: 1 }} />
              <Button
                size="small"
                variant="text"
                onClick={handleCloseAddComp}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                type="submit"
              >
                Send
              </Button>
            </CardActions>
          </ValidatorForm>
        )}
        {props?.session?.me?.activeOrg?.member === 'RWN' && (
          <ValidatorForm onSubmit={contact}>
            <DialogTitle>
              <Typography>Message to Agent</Typography>
            </DialogTitle>
            <CardContent>
              <BlockUi
                blocking={loader}
                loader={
                  <PacmanLoader
                    color={'var(--primary)'}
                    loading={true}
                  />
                }
                message={
                  <Box sx={{ color: '#368cff' }}>Sending Message</Box>
                }
              >
                <Grid container direction="row" spacing={2}>
                  <Grid size={{ sm: 6 }}>
                    <TextValidator
                      fullWidth
                      variant="outlined"
                      // margin='dense'
                      label="First Name"
                      value={firstname}
                      onChange={(event) =>
                        setFirstname(event.target.value)
                      }
                      required={true}
                      validators={[
                        'required',
                        'maxStringLength: 20',
                        'matchRegexp:^[A-z ]+$',
                      ]}
                      errorMessages={[
                        'This field is required',
                        'You cannot exceed 20 characters',
                        'This field only allows characters in the alphabet and spaces',
                      ]}
                    />
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <TextValidator
                      fullWidth
                      variant="outlined"
                      // margin='dense'
                      label="Last Name"
                      value={lastname}
                      onChange={(event) =>
                        setLastname(event.target.value)
                      }
                      required={true}
                      validators={[
                        'required',
                        'maxStringLength: 20',
                        'matchRegexp:^[A-z ]+$',
                      ]}
                      errorMessages={[
                        'This field is required',
                        'You cannot exceed 20 characters',
                        'This field only allows characters in the alphabet and spaces',
                      ]}
                    />
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <TextValidator
                      fullWidth
                      variant="outlined"
                      // margin='dense'
                      label="Email Address"
                      value={email}
                      onChange={(event) =>
                        setEmail(event.target.value)
                      }
                      required={true}
                      validators={[
                        'required',
                        'maxStringLength: 50',
                        'isEmail',
                      ]}
                      errorMessages={[
                        'This field is required',
                        'You cannot exceed 50 characters',
                        'Not a valid email address',
                      ]}
                    />
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <TextValidator
                      fullWidth
                      variant="outlined"
                      // margin='dense'
                      label="Phone"
                      value={phone}
                      onChange={(event) =>
                        setPhone(event.target.value)
                      }
                      required={true}
                      validators={[
                        'required',
                        'matchRegexp:^[0-9()-]+$',
                      ]}
                      errorMessages={[
                        'This field is required',
                        'Not a valid phone number',
                      ]}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextValidator
                      fullWidth
                      variant="outlined"
                      // margin='dense'
                      label="Comments"
                      value={message}
                      onChange={(event) =>
                        setMessage(event.target.value)
                      }
                      required={true}
                      multiline={true}
                      rows={4}
                      validators={[
                        'required',
                        'maxStringLength: 3000',
                      ]}
                      errorMessages={[
                        'This field is required',
                        'You cannot exceed 3000 characters',
                      ]}
                    />
                  </Grid>
                </Grid>
              </BlockUi>
            </CardContent>
            <Divider />
            <CardActions>
              <Typography style={{ flex: 1 }} />
              <Button
                size="small"
                variant="text"
                onClick={handleCloseAddComp}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                type="submit"
              >
                Send
              </Button>
            </CardActions>
          </ValidatorForm>
        )}
      </Dialog>
    </>
  )
}

export default withSession(ContactAgentComponent)
